import React from 'react';
import { Col, Row } from 'reactstrap';
import dayjs from 'dayjs';
import { formatDuration, weeklyPlannerDayDisplayFormat } from 'tsx/libs/dayjs';

import Link from '~components/Link';
import PopOver from '~components/pop-over/PopOver';

import { Appointment } from '../../lib/common';
import WorkerQualifications from '../common/WorkerQualifications';

interface ComponentProps {
  isOpen?: boolean;
  target: string;
  toggle?: () => void;
  appointment: Appointment;
  isDashboardView?: boolean;
}

const AppointmentDetails: React.FC<ComponentProps> = ({
  isOpen = false,
  target,
  toggle,
  appointment,
  isDashboardView = false,
}) => {
  const {
    client,
    status,
    duration,
    date,
    flexibility,
    qualification_level,
    service_type,
    start_time,
    end_time,
    notes,
    notes_admin,
    location_override,
  } = appointment;

  const formattedDate = dayjs(date).format(weeklyPlannerDayDisplayFormat);
  const suburb = location_override?.suburb ?? client.suburb;

  const buildRosteringNotes = () => {
    if (notes_admin) {
      const notesArr = notes_admin.split('<br>').filter(Boolean);
      return (
        <ul className="ps-3">
          {notesArr.map((note, idx) => (
            <li key={idx}>{note}</li>
          ))}
        </ul>
      );
    }
    return <em>No Rostering Notes</em>;
  };

  const renderPlannerView = () => (
    <>
      <Row>
        <Col className={`mb-3 text-end ${flexibility ? 'text-success' : 'text-danger'}`}>
          {flexibility ? 'Flexible with Times' : 'Not Flexible with Times'}
        </Col>
      </Row>

      <Row className="mb-3">
        {qualification_level?.length === 0 ? (
          <Col>No Qualifications Required</Col>
        ) : (
          <Col>
            Qualifications Required:
            <WorkerQualifications isCondensed qualifications={qualification_level} />
          </Col>
        )}
      </Row>
      <Row title={notes} className="mb-3">
        <Row className="notes">Notes: {notes}</Row>
      </Row>
      <Row className="mb-2">
        <Row lg={4}>Roster Notes:</Row>
        <Row>{buildRosteringNotes()}</Row>
      </Row>
      <Row>
        {dayjs().isAfter(date) && (
          <div className="text-danger">
            <p className="fw-bold mb-0">This appointment date has passed.</p>
            <p>Do not remove unless there has been a clerical / data entry error.</p>
          </div>
        )}
      </Row>
    </>
  );

  return (
    <PopOver
      isOpen={isOpen}
      toggle={toggle}
      target={target}
      placement="right"
      autoFlip={true}
      className="popover show bs-popover-auto popover-inner appointment-popover"
      styleOverrides={{ padding: 0 }}
    >
      <div aria-label="Appointment Details Popover" role="tooltip">
        <h3 className="popover-header">Appointment Details</h3>
        <div className="pop-body popover-body">
          <Row className="title mb-2">{client?.full_name} </Row>
          <Row>
            <Col lg={3}>Status:</Col>
            <Col className="text-end">{status?.name}</Col>
          </Row>
          <Row>
            <Col lg={3}>Referrer:</Col>
            <Col className="text-end">{client?.referrer_name || '-'}</Col>
          </Row>
          <Row>
            <Col lg={3}>Service:</Col>
            <Col className="text-end">{service_type?.name}</Col>
          </Row>
          <Row>
            <Col lg={3}>Suburb:</Col>
            <Col className="text-end">{suburb}</Col>
          </Row>
          <Row>
            <Col lg={3}>Day:</Col>
            <Col className="text-end">{formattedDate}</Col>
          </Row>
          <Row>
            <Col lg={3}>Time:</Col>
            <Col className="text-end">
              {start_time} - {end_time} ({formatDuration(duration)})
            </Col>
          </Row>
          {isDashboardView ? (
            <Row className="mt-4 text-end">
              <Col>
                <Link to={`/appointments/${appointment?.id}`}>View Appointment Details</Link>
              </Col>
            </Row>
          ) : (
            renderPlannerView()
          )}
        </div>
      </div>
    </PopOver>
  );
};

export default AppointmentDetails;
